import './MessagingChannelPreview.css'
import {
  Avatar,
  MessageDeliveryStatus,
  type ChannelPreviewUIComponentProps,
  useChatContext,
} from 'stream-chat-react'
import classNames from 'classnames'
import { navigate } from 'gatsby'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import type { MouseEventHandler } from 'react'
import type { Channel, ChannelMemberResponse } from 'stream-chat'
import type { StreamChatGenerics } from '../../types'
import {
  displayNameFromStreamUser,
  lookupOwnerDirectoryName,
} from '../../../Calendar/ownerDirectory'
import { getChannelLastMessagePreview } from '../../../../utils/channelLastMessagePreview'
import {
  formatPhoneFromStreamId,
  isReservationChannelId,
  reservationChannelTitle,
  resolveConversationTitle,
} from '../../../../utils/streamUserId'

/** Real portrait placeholders (randomuser.me) when the peer has no Stream `image`. */
const PEER_PLACEHOLDER_PORTRAITS = [
  'https://randomuser.me/api/portraits/women/44.jpg',
  'https://randomuser.me/api/portraits/men/75.jpg',
  'https://randomuser.me/api/portraits/women/63.jpg',
  'https://randomuser.me/api/portraits/women/32.jpg',
  'https://randomuser.me/api/portraits/men/32.jpg',
  'https://randomuser.me/api/portraits/women/68.jpg',
  'https://randomuser.me/api/portraits/men/52.jpg',
  'https://randomuser.me/api/portraits/women/17.jpg',
  'https://randomuser.me/api/portraits/men/86.jpg',
  'https://randomuser.me/api/portraits/women/90.jpg',
  'https://randomuser.me/api/portraits/men/41.jpg',
  'https://randomuser.me/api/portraits/women/12.jpg',
]

function hashToUInt(seed: string): number {
  let h = 0
  for (let i = 0; i < seed.length; i++) {
    h = (Math.imul(31, h) + seed.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

export function peerPortraitPlaceholder(seed: string): string {
  const i = hashToUInt(seed || 'peer') % PEER_PLACEHOLDER_PORTRAITS.length
  return PEER_PLACEHOLDER_PORTRAITS[i]
}

/** `/message` thread route — not `/messages` (inbox list). */
function isCrmMessageThreadPathname(pathname: string) {
  return pathname === '/message' || pathname.startsWith('/message/')
}

const getTimeStamp = (channel: Channel) => {
  let lastHours = channel.state.last_message_at?.getHours()
  let lastMinutes: string | number | undefined =
    channel.state.last_message_at?.getMinutes()
  let half = 'AM'

  if (lastHours === undefined || lastMinutes === undefined) {
    return ''
  }

  if (lastHours > 12) {
    lastHours = lastHours - 12
    half = 'PM'
  }

  if (lastHours === 0) lastHours = 12
  if (lastHours === 12) half = 'PM'

  if (lastMinutes.toString().length === 1) {
    lastMinutes = `0${lastMinutes}`
  }

  return `${lastHours}:${lastMinutes} ${half}`
}

/** Other participant(s) in this channel — excludes the connected client when we know their id. */
const getOtherMembers = (
  channel: Channel,
  selfId: string | undefined,
): ChannelMemberResponse[] => {
  const all = Object.values(channel.state.members)
  if (!selfId) {
    return all.filter((m) => m.user?.id)
  }
  return all.filter((m) => m.user?.id && m.user.id !== selfId)
}

/** `channel.id`, or the id segment of `channel.cid` (`messaging:<id>`). */
function messagingChannelIdString(channel: Channel): string {
  const id = (channel.id || '').trim()
  if (id) return id
  const cid = (channel.cid || '').trim()
  const colon = cid.indexOf(':')
  return colon >= 0 ? cid.slice(colon + 1).trim() : cid
}

/**
 * Stream 1:1 channel ids join two user ids with `--` (sorted). A naive `split('--')`
 * breaks when a user id itself contains `--`. Anchor on `selfId` and capture the peer.
 * Also used when `channel.state.members` has >2 keys (Stream can briefly over-report) so
 * we still resolve the other participant for `/message/?c=`.
 */
function parseOneOnOnePeerFromChannelId(
  channel: Channel,
  selfId: string | undefined,
): string | undefined {
  if (!selfId) return undefined
  const raw = messagingChannelIdString(channel)
  if (!raw || !raw.includes('--')) return undefined

  const escaped = selfId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const anchored = new RegExp(`^(?:${escaped})--(.+)$|^(.+)--(?:${escaped})$`)
  const m = raw.match(anchored)
  const peer = (m?.[1] || m?.[2] || '').trim()
  if (peer) return peer

  const parts = raw.split('--').map((p) => p.trim()).filter(Boolean)
  if (parts.length < 2) return undefined
  for (const id of parts) {
    if (id && id !== selfId) return id
  }
  return undefined
}

/**
 * User id for `/message/?c=` deep links. Member map keys are available as soon
 * as Stream returns the channel; `member.user` can lag, which used to skip navigation.
 * If the **latest** message is from self, we still need the peer — scan older messages.
 */
export function resolvePeerIdForMessageRoute(
  channel: Channel,
  selfId: string | undefined,
): string | undefined {
  const raw = messagingChannelIdString(channel)
  if (isReservationChannelId(raw)) {
    return raw
  }

  const memberIds = Object.keys(channel.state?.members || {}).filter(
    (id) => id && id !== selfId,
  )
  if (memberIds.length > 0) {
    return memberIds[0]
  }
  const others = getOtherMembers(channel, selfId)
  const fromUser = others[0]?.user?.id
  if (fromUser) {
    return fromUser
  }
  const msgs = channel.state.messages
  if (msgs?.length) {
    for (let i = msgs.length - 1; i >= 0; i -= 1) {
      const uid = msgs[i]?.user?.id
      if (uid && (!selfId || uid !== selfId)) {
        return uid
      }
    }
  }
  return parseOneOnOnePeerFromChannelId(channel, selfId)
}

const formatMemberLabel = (m: ChannelMemberResponse) =>
  m.user?.name?.trim() || m.user?.id || ''

/** Same hint as `PageMessage` when calendar links include `?peer=`. */
function readMessageRoutePeerHint(peerId: string | undefined): string | null {
  if (typeof window === 'undefined' || !peerId) {
    return null
  }
  if (!isCrmMessageThreadPathname(window.location.pathname)) {
    return null
  }
  const p = new URLSearchParams(window.location.search)
  const c = p.get('c')
  const peer = p.get('peer')
  if (!c || c !== peerId || !peer?.trim()) {
    return null
  }
  return peer.trim()
}

/** Prefer direct member state so 1:1 shows the peer, not channel slug / last-initial confusion. */
const getPeerConversationTitle = (
  channel: Channel,
  selfId: string | undefined,
): string => {
  const raw = messagingChannelIdString(channel)
  if (isReservationChannelId(raw)) {
    return reservationChannelTitle(channel.data || {})
  }

  const others = getOtherMembers(channel, selfId)
  if (others.length === 1) {
    return formatMemberLabel(others[0]) || ''
  }
  if (others.length > 1) {
    return others.map(formatMemberLabel).filter(Boolean).join(', ')
  }
  return ''
}

function shouldSyncMessageUrlFromPath(pathname: string | undefined) {
  if (!pathname) return false
  return (
    pathname === '/dashboard' ||
    pathname.startsWith('/dashboard/') ||
    isCrmMessageThreadPathname(pathname) ||
    pathname === '/messages' ||
    pathname.startsWith('/messages/')
  )
}

/** Open CRM message route with the primary peer id (`?c=` matches `PageMessage` / `Chat`). */
function goToMessageConversationForChannel(
  listParentPathname: string | undefined,
  channel: Channel,
  selfId: string | undefined,
) {
  const pathname =
    listParentPathname ||
    (typeof window !== 'undefined' ? window.location.pathname : '')
  if (typeof window === 'undefined' || !shouldSyncMessageUrlFromPath(pathname)) {
    return
  }
  const peerId = resolvePeerIdForMessageRoute(channel, selfId)
  if (!peerId) return

  const target = `/message/?c=${encodeURIComponent(peerId)}`
  if (isCrmMessageThreadPathname(pathname)) {
    void navigate(target, { replace: true })
  } else {
    void navigate(target)
  }
}

function DoubleDeliveredChecks({ read }: { read: boolean }) {
  const tooltipIdRef = useRef<string>()
  if (!tooltipIdRef.current) {
    tooltipIdRef.current = `channel-preview-seen-${Math.random()
      .toString(36)
      .slice(2, 11)}`
  }
  const tooltipId = tooltipIdRef.current

  const anchorRef = useRef<HTMLSpanElement>(null)
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState({ top: 0, left: 0 })

  const updateCoords = useCallback(() => {
    const el = anchorRef.current
    if (!el || typeof window === 'undefined') return
    const r = el.getBoundingClientRect()
    setCoords({
      top: r.top - 8,
      left: r.left + r.width / 2,
    })
  }, [])

  const openTooltip = useCallback(() => {
    updateCoords()
    setOpen(true)
  }, [updateCoords])

  const closeTooltip = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    updateCoords()
    const onReposition = () => updateCoords()
    window.addEventListener('scroll', onReposition, true)
    window.addEventListener('resize', onReposition)
    return () => {
      window.removeEventListener('scroll', onReposition, true)
      window.removeEventListener('resize', onReposition)
    }
  }, [open, updateCoords])

  const checks = (
    <span
      className={classNames(
        'channel-preview__receipts',
        read && 'channel-preview__receipts--read',
      )}
      role='img'
      aria-label={read ? 'Seen' : 'Delivered'}
    >
      <svg
        className='channel-preview__check'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <polyline points='20 6 9 17 4 12' />
      </svg>
      <svg
        className='channel-preview__check channel-preview__check--overlap'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <polyline points='20 6 9 17 4 12' />
      </svg>
    </span>
  )

  const tooltipLabel = read ? 'Seen' : 'Delivered'

  const tooltip =
    open && typeof document !== 'undefined'
      ? createPortal(
          <span
            id={tooltipId}
            className='channel-preview__read-popover channel-preview__read-popover--portal'
            role='tooltip'
            style={{ top: coords.top, left: coords.left }}
          >
            {tooltipLabel}
          </span>,
          document.body,
        )
      : null

  return (
    <>
      <span
        ref={anchorRef}
        className='channel-preview__receipts-anchor'
        tabIndex={0}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={openTooltip}
        onMouseLeave={closeTooltip}
        onFocus={openTooltip}
        onBlur={closeTooltip}
      >
        {checks}
      </span>
      {tooltip}
    </>
  )
}

export type MessagingChannelPreviewProps =
  ChannelPreviewUIComponentProps<StreamChatGenerics> & {
    /** Optional extra handler (e.g. mobile sidebar) — runs after channel selection. */
    onClick?: MouseEventHandler
    /** CRM shell: current route; when under `/dashboard` or `/message`, clicks navigate to `/message/?c=`. */
    listParentPathname?: string
  }

const MessagingChannelPreview = (props: MessagingChannelPreviewProps) => {
  const {
    channel,
    displayTitle,
    displayImage,
    setActiveChannel,
    onSelect,
    onClick,
    watchers,
    messageDeliveryStatus,
    listParentPathname,
  } = props
  const { channel: activeChannel, client, setActiveChannel: setActiveChannelCtx } =
    useChatContext<StreamChatGenerics>()

  const selfId = client.userID ?? client.user?.id
  const peerTitle = getPeerConversationTitle(channel, selfId)
  const previewOthers = getOtherMembers(channel, selfId)

  const peerId = useMemo(() => {
    return (
      resolvePeerIdForMessageRoute(channel, selfId) ??
      getOtherMembers(channel, selfId)[0]?.user?.id ??
      undefined
    )
  }, [channel, selfId])

  const [queriedPeer, setQueriedPeer] = useState<{
    name: string | null
    email: string | null
  }>({ name: null, email: null })

  useEffect(() => {
    if (!client || !peerId) {
      setQueriedPeer({ name: null, email: null })
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const { users } = await client.queryUsers(
          { id: { $eq: peerId } },
          { id: 1, name: 1 },
          { limit: 1 },
        )
        if (cancelled) {
          return
        }
        const user = users[0] as { email?: unknown } | undefined
        setQueriedPeer({
          name: displayNameFromStreamUser(user),
          email: typeof user?.email === 'string' ? user.email : null,
        })
      } catch {
        if (!cancelled) {
          setQueriedPeer({ name: null, email: null })
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [client, peerId, channel.cid])

  const urlPeerHint = readMessageRoutePeerHint(peerId)

  const avatarSeed =
    resolvePeerIdForMessageRoute(channel, selfId) ??
    previewOthers[0]?.user?.id ??
    channel.cid ??
    peerTitle ??
    'conversation'
  const avatarImage =
    (displayImage && displayImage.trim()) || peerPortraitPlaceholder(avatarSeed)

  const memberDisplay =
    peerTitle && peerId && peerTitle !== peerId ? peerTitle : ''

  const title = resolveConversationTitle(
    [
      queriedPeer.name,
      lookupOwnerDirectoryName(peerId),
      urlPeerHint,
      memberDisplay,
      displayTitle,
      channel.data?.name,
      queriedPeer.email,
      formatPhoneFromStreamId(peerId),
    ],
    { peerId },
  )

  const handleClick = (e: React.MouseEvent) => {
    onClick?.(e)
    if (onSelect) {
      onSelect(e)
    } else if (setActiveChannel) {
      setActiveChannel(channel, watchers)
    }
    goToMessageConversationForChannel(listParentPathname, channel, selfId)
  }

  const handleRemoveConversation = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const uid = client.userID ?? client.user?.id
    if (!uid) return

    const peerForRoute = resolvePeerIdForMessageRoute(channel, selfId)
    const leaveMessageRoute =
      typeof window !== 'undefined' &&
      isCrmMessageThreadPathname(listParentPathname ?? '') &&
      Boolean(peerForRoute) &&
      new URLSearchParams(window.location.search).get('c') === peerForRoute

    try {
      await channel.hide(uid)
    } catch (err) {
      console.error('Failed to hide channel', err)
      return
    }

    if (channel.cid === activeChannel?.cid) {
      setActiveChannelCtx?.(undefined)
    }
    if (leaveMessageRoute) {
      void navigate('/dashboard', { replace: true })
    }
  }

  const isActive = channel?.cid === activeChannel?.cid

  const hasMessageHistory = (channel.state.messages?.length ?? 0) > 0
  const previewText = getChannelLastMessagePreview(channel)

  const showReadReceipts =
    messageDeliveryStatus === MessageDeliveryStatus.READ ||
    messageDeliveryStatus === MessageDeliveryStatus.DELIVERED

  return (
    <div
      className={classNames(
        'channel-preview__container',
        isActive && 'selected',
      )}
      role='option'
      aria-selected={isActive}
      onClick={handleClick}
      tabIndex={isActive ? 0 : -1}
    >
      <Avatar image={avatarImage} name={title} size={36} />
      <div className='channel-preview__content-wrapper'>
        <div className='channel-preview__content-top'>
          <p className='channel-preview__content-name' title={title}>
            {title}
          </p>
          <div className='channel-preview__content-trailing'>
            <p className='channel-preview__content-time'>{getTimeStamp(channel)}</p>
            {showReadReceipts ? (
              <DoubleDeliveredChecks
                read={messageDeliveryStatus === MessageDeliveryStatus.READ}
              />
            ) : null}
            <button
              type='button'
              className='channel-preview__delete'
              aria-label='Remove conversation'
              title='Remove conversation'
              onClick={handleRemoveConversation}
            >
              <svg viewBox='0 0 24 24' fill='none' aria-hidden>
                <path
                  d='M18 6L6 18M6 6l12 12'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                />
              </svg>
            </button>
          </div>
        </div>
        <div
          className={
            hasMessageHistory
              ? 'channel-preview__content-message'
              : 'channel-preview__content-message channel-preview__content-message--empty'
          }
        >
          {previewText || null}
        </div>
      </div>
    </div>
  )
}

export default MessagingChannelPreview
